import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { isEmpty, defaultTo } from 'lodash';
import memoize from 'lodash/memoize';
import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  CellWithInfoIcon,
  ColGroup,
  CustomDynamicResultTable,
  CustomizeView,
  FormattedAddress,
  FormattedDate,
  HeadCell,
  InvoiceUtil,
  SearchResultColumn,
  SimpleTD,
  TableBody,
  TableHead,
} from '@css-xp/base';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { InvoicePaymentMethod, SortDirection, Refunds } from '@css-xp/graphtypes/src/generated-types-new';
import { DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import {
  SoldToAddressDetailsLink,
  errorStateProp,
  emptyStateSearchTabs,
  getSelectedColumns,
  FormatCurrency,
  NoData,
  PPC_TABS,
  TableHeading,
  BillingProfileNameFromId,
} from '@css-xp/ppc-common';
import { SEARCH_PAGE_LOADER_OPTIONS, routePaths } from '@css-xp/ppc-constants';
import {
  getInvoiceStatus,
  InvoiceCell,
  RefundChildRow,
  InvoiceTableColumns,
  invoiceTableCommonHeadersMap,
} from '@css-xp/ppc-invoice-table';
import { PartnerSearchContext } from '@css-xp/ppc-types';
import { getRouteUrl, getSearchTextVariants } from '@css-xp/ppc-utils';
import { emptyStatePropForWrongSearch } from '@css-xp/ppc-common/src/state-props';
import { FetchInvoicesForTransactionAccountWithContext } from '../../queries/fetch-invoices-for-search';
import './invoice-search.scss';

type ColumnList = HeadCell & SearchResultColumn;

// columns to show for design iteration 1
const defaultColumns = [
  InvoiceTableColumns.INVOICE_NUMBER,
  InvoiceTableColumns.STATUS,
  InvoiceTableColumns.AMOUNT,
  InvoiceTableColumns.ISSUED,
  InvoiceTableColumns.DUE,
  InvoiceTableColumns.CUSTOMER,
  InvoiceTableColumns.PO_NUMBER,
];

export const getIsFreeInvoice = memoize((paymentMethod?: any | null) => {
  return paymentMethod === InvoicePaymentMethod.NOPAYMENT;
});

const getCompany = (
  transactionAccountId: string,
  shipToParty?: generatedTypesNew.Maybe<generatedTypesNew.InvoiceBillAndShipTo>
) => {
  if (!shipToParty || !shipToParty.fullName) {
    return <NoData />;
  }

  return (
    <SoldToAddressDetailsLink txaId={transactionAccountId} companyName={shipToParty.fullName}>
      <div> {shipToParty?.fullName} </div>
    </SoldToAddressDetailsLink>
  );
};

const getPaymentMethod = (paymentId: string | undefined | null, creditCard: any, invoiceDueDays: any) => {
  if (paymentId) {
    if (paymentId === InvoicePaymentMethod.PAIDCC || paymentId === InvoicePaymentMethod.STRIPE_CARD) {
      return creditCard?.last4Numbers ? `**** **** **** ${creditCard.last4Numbers}` : '';
    }
    if (paymentId === InvoicePaymentMethod.DEFERRED) {
      return `Payment on terms: due in ${invoiceDueDays} days`;
    }
    if (paymentId === InvoicePaymentMethod.PAIDONACCOUNT || paymentId === InvoicePaymentMethod.PAID_BANK) {
      return 'Pay on Account';
    }
  }
  return 'Not available';
};

const getFormattedAddress = (address: any) => (
  <span className="line-clamp">
    <FormattedAddress
      addressLine1={address?.address1}
      addressLine2={address?.address2}
      city={address?.city}
      country={address?.country}
      postcode={address?.postalCode}
      state={address?.state}
    />
  </span>
);

const getSoldToAddress = (
  transactionAccountId: string,
  shipToParty?: generatedTypesNew.Maybe<generatedTypesNew.InvoiceBillAndShipTo>
) => {
  const shipToId = shipToParty?.id;
  const address = shipToParty?.address;

  if (shipToId && address) {
    return (
      <SoldToAddressDetailsLink txaId={transactionAccountId} shipToId={shipToId}>
        {getAddress(address)}
      </SoldToAddressDetailsLink>
    );
  }

  return getAddress(address);
};

const getAddress = (address: any) => {
  if (!address) {
    return 'No address information';
  } else {
    return getFormattedAddress(address);
  }
};

const getDueOnCell = (cellName: string) => {
  return <CellWithInfoIcon cellName={cellName} tooltipText="Overdues in open invoices will be marked in red" />;
};

const headersMap = (isDesignIteration1Enabled?: boolean) => {
  const commonHeadersMap = invoiceTableCommonHeadersMap();

  const getAmountCell = () => {
    if (isDesignIteration1Enabled) {
      return <th className="text-align-right">{InvoiceTableColumns.AMOUNT}</th>;
    }
  };

  return {
    ...commonHeadersMap,
    [InvoiceTableColumns.DUE]: {
      ...commonHeadersMap[InvoiceTableColumns.DUE],
      renderComponent: getDueOnCell('Due'),
    },
    [InvoiceTableColumns.AMOUNT]: {
      ...commonHeadersMap[InvoiceTableColumns.AMOUNT],
      renderComponent: getAmountCell(),
    },
  };
};

const getHead = (columnsToShow: string[], isDesignIteration1Enabled?: boolean) => {
  const columnHeaders = headersMap(isDesignIteration1Enabled);

  if (isDesignIteration1Enabled) {
    //@ts-ignore
    const updatedColumns = columnsToShow.map((column) => columnHeaders[column]);
    return updatedColumns;
  }

  const allColumns = [
    columnHeaders[InvoiceTableColumns.INVOICE_NUMBER],
    columnHeaders[InvoiceTableColumns.STATUS],
    columnHeaders[InvoiceTableColumns.DUE],
    columnHeaders[InvoiceTableColumns.ISSUED],
    columnHeaders[InvoiceTableColumns.AMOUNT],
    columnHeaders[InvoiceTableColumns.COMPANY],
    columnHeaders[InvoiceTableColumns.SOLD_TO_ADDRESS],
    columnHeaders[InvoiceTableColumns.PAYMENT_METHOD],
    columnHeaders[InvoiceTableColumns.PO_NUMBER],
  ];

  return allColumns;
};

const getColumnData = (
  visibleColumns: ColumnList[],
  invoice: generatedTypesNew.Invoice,
  isDesignIteration1Enabled?: boolean
) => {
  const {
    id,
    invoiceNumber,
    status,
    dueDate,
    total,
    grandTotal,
    paymentCurrency,
    pricingCurrency,
    createdDate,
    paymentMethod,
    creditCard,
    shipTo,
    refunds,
    transactionAccountId,
    invoiceDueDays,
    purchaseOrderNumber,
    billingGroupId,
  } = invoice;
  const invoiceSlug =
    transactionAccountId && invoiceNumber
      ? getRouteUrl(routePaths.invoiceDetails, {
          txaId: transactionAccountId,
          invoiceId: invoiceNumber,
        })
      : '';
  const rowData = visibleColumns.map((column) => {
    switch (column.name) {
      case InvoiceTableColumns.INVOICE_NUMBER:
        return (
          <td>
            <InvoiceCell
              invoiceId={id}
              status={status}
              invoiceNumber={invoiceNumber || ''}
              paymentError={!!invoice?.dunningStatus?.length}
              refunds={refunds as Refunds[]}
              transactionAccountId={transactionAccountId as string}
              grandTotal={total}
              invoiceLink={invoiceSlug}
            />
          </td>
        );
      case InvoiceTableColumns.STATUS:
        return <td>{getInvoiceStatus(status)}</td>;
      case InvoiceTableColumns.DUE:
        return (
          <td>
            {dueDate ? (
              <div
                className={classNames({
                  'overdue-date': InvoiceUtil.isDueDateOverdue(status ?? '', !!invoice?.dunningStatus?.length, dueDate),
                })}
              >
                <FormattedDate date={dueDate} format={NEW_DATE_FORMAT} />
              </div>
            ) : (
              <NoData />
            )}
          </td>
        );
      case InvoiceTableColumns.ISSUED:
        return <td>{createdDate ? <FormattedDate date={createdDate} format={NEW_DATE_FORMAT} /> : <NoData />}</td>;
      case InvoiceTableColumns.AMOUNT:
        return (
          <td className={classNames('invoice-amount-cell', { 'text-align-right': isDesignIteration1Enabled })}>
            {!getIsFreeInvoice(paymentMethod) && grandTotal ? (
              <FormatCurrency
                value={grandTotal}
                currency={defaultTo(pricingCurrency, paymentCurrency)}
                showCurrencyCode
                showCommas
              />
            ) : (
              'Free'
            )}
          </td>
        );
      case InvoiceTableColumns.CUSTOMER:
      case InvoiceTableColumns.COMPANY:
        return <td>{getCompany(transactionAccountId as string, shipTo)}</td>;
      case InvoiceTableColumns.SOLD_TO_ADDRESS:
        return <td>{getSoldToAddress(transactionAccountId as string, shipTo)}</td>;
      case InvoiceTableColumns.PAYMENT_METHOD:
        return (
          <SimpleTD
            text={getPaymentMethod(paymentMethod, creditCard, invoiceDueDays)}
            className={classNames({
              'not-applicable': getIsFreeInvoice(paymentMethod) || !paymentMethod,
            })}
          />
        );
      case InvoiceTableColumns.PO_NUMBER:
        return <td>{purchaseOrderNumber ? purchaseOrderNumber : <NoData />}</td>;
      case InvoiceTableColumns.BILLING_PROFILE:
        return (
          <td>
            <BillingProfileNameFromId
              id={billingGroupId}
              txaId={transactionAccountId || ''}
              showBillingProfileNumber={true}
            />
          </td>
        );
    }
  });
  return rowData;
};
export const getFilterOptions = (query: string) => {
  const purchaseOrderOptions = getSearchTextVariants('purchaseOrderNumber', query);
  const shipToAddressOptions = getSearchTextVariants('containsNameMultiMatch', query);
  return {
    or: [
      ...purchaseOrderOptions,
      ...shipToAddressOptions,
      // FIXME :: Enable it post hydra fixes the issue related to 400 error
      // 'cloudSiteHostnamePartial': query,
    ],
  };
};

const defaultSortOrder = {
  sortFields: ['createdDate', 'id'],
  sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
};

export const defaultSearchOptions = {
  first: 8,
  offset: 0,
  sort: defaultSortOrder,
};

interface InvoiceSearchWithContextProps {
  query: string;
  transactionAccountId: string;
  partnerSearchContext: generatedTypesNew.PartnerSearchContext;
  isDesignIteration1Enabled?: boolean;
  columnsToShow?: string[];
}

export const InvoiceSearchWithContext = memo(
  ({
    transactionAccountId,
    query,
    partnerSearchContext,
    isDesignIteration1Enabled,
    columnsToShow = defaultColumns,
  }: InvoiceSearchWithContextProps) => {
    const [hasNextPage, setHasNextPage] = useState<boolean>(false);
    const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
    const [tableRows, setTableRows] = useState<Array<unknown>>([]);
    const [previousSearchContext, setPreviousSearchContext] = useState<PartnerSearchContext>();
    const [tableLevelLoading, setTableLevelLoading] = useState<boolean>(true);
    const [columnsList, setColumnList] = useState<Array<ColumnList>>(getHead(columnsToShow, isDesignIteration1Enabled));
    const { loading, data, refetch } = useQuery<
      generatedTypesNew.FetchInvoicesForTransactionAccountWithContextQuery,
      generatedTypesNew.FetchInvoicesForTransactionAccountWithContextQueryVariables
    >(FetchInvoicesForTransactionAccountWithContext, {
      variables: {
        id: transactionAccountId,
        partnerSearchContext,
        queryString: query,
        pageParams: {
          size: DEFAULT_ROWS_PER_PAGE,
        },
        isOrgSearchEnabled: true,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
      displayName: 'FetchInvoicesForTransactionAccountWithContext',
    });

    // Once the search context is changed, need to remove load more button and enable table level loading
    useEffect(() => {
      if (loading) {
        setTableRows([]);
      }
      setHasNextPage(false);
      setTableLevelLoading(true);
    }, [partnerSearchContext]);

    useEffect(() => {
      if (!loading) {
        const nodes = data?.transactionAccount?.partnerInvoices?.nodes ?? [];
        if (partnerSearchContext === previousSearchContext) {
          // This is for load more on same search context.
          setTableRows([...tableRows, ...nodes]);
        } else {
          setTableRows([...nodes]);
        }
        setHasNextPage(data?.transactionAccount?.partnerInvoices?.pageInfo?.hasNextPage ?? false);
        setEndCursor(data?.transactionAccount?.partnerInvoices?.pageInfo?.endCursor ?? undefined);
        setPreviousSearchContext(partnerSearchContext);
      }
    }, [data, loading]);

    const onLoadMore = useCallback(() => {
      if (endCursor) {
        setTableLevelLoading(false);
        setHasNextPage(false);
        refetch({
          pageParams: {
            size: DEFAULT_ROWS_PER_PAGE,
            cursor: endCursor,
          },
        });
      }
    }, [refetch, endCursor]);

    const refundsRoutePPC = getRouteUrl(routePaths.refunds, { txaId: transactionAccountId || '' });
    const hasNoResults = tableRows.length === 0;

    const props = {
      children: (
        <>
          <ColGroup columns={getSelectedColumns(columnsList)} />
          <TableHead columns={getSelectedColumns(columnsList)} />
          <TableBody>
            {(tableRows ?? [])?.map((invoice: any, index: number) => {
              const { refunds } = invoice;

              return (
                <>
                  <tr data-testid={`invoice-table-row-${index}`}>
                    {getColumnData(getSelectedColumns(columnsList), invoice, isDesignIteration1Enabled).map(
                      (node) => node
                    )}
                  </tr>
                  <>
                    {!isEmpty(refunds) &&
                      (refunds ?? []).map((ccpCreditNote: Refunds, indexRefund: number) => {
                        return (
                          <RefundChildRow
                            key={`invoice-refund-row${indexRefund}`}
                            refund={ccpCreditNote}
                            isSelected={false}
                            index={index}
                            refundDetailsLink={`${refundsRoutePPC}${encodeURIComponent(
                              ccpCreditNote.creditNoteNumber || ccpCreditNote.id
                            )}`}
                            shouldDisableSidebar={true}
                            visibleColumns={getSelectedColumns(columnsList)}
                            isDesignIteration1Enabled={isDesignIteration1Enabled}
                          />
                        );
                      })}
                  </>
                </>
              );
            })}
          </TableBody>
        </>
      ),
      noResults: hasNoResults,
      isLoading: tableLevelLoading && loading,
      error: '',
      innerHeading: !isDesignIteration1Enabled && <TableHeading>{PPC_TABS.INVOICES}</TableHeading>,
      selectableResults: true,
      isPaginationEnabled: false,
      testId: 'search-invoice-tab',
      className: classNames('search-invoice-tab', {
        'search-invoice-tab-v2': isDesignIteration1Enabled,
      }),
      emptyStateProps: isDesignIteration1Enabled ? emptyStatePropForWrongSearch : emptyStateSearchTabs,
      errorStateProps: errorStateProp,
      showLoadMore: hasNextPage,
      onLoadMore: onLoadMore,
      loadMoreLoading: !tableLevelLoading && loading,
      loaderOptions: isDesignIteration1Enabled ? SEARCH_PAGE_LOADER_OPTIONS : {},
    };

    return (
      <div style={{ order: hasNoResults ? 1 : 0 }} data-testid="invoice-table-list">
        <CustomDynamicResultTable
          {...props}
          customiseColumnView={
            isDesignIteration1Enabled ? (
              <></>
            ) : (
              <CustomizeView
                columns={columnsList}
                columnCount={columnsList.length}
                updatedColumnView={(record: Array<HeadCell>) => setColumnList([...record] as Array<ColumnList>)}
              />
            )
          }
        />
      </div>
    );
  }
);
